import React, { useContext, useEffect, useState } from "react";

import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from "@mui/material";
import clsx from "clsx";
import ReactJson, { ThemeKeys } from "react-json-view";

import {
  AdminListWebhooksResponse,
  WebhookResponse,
} from "@safetyculture/s12-apis-connect-web/s12/webhooks/v1/webhooks_pb";

import { callOptionsHeaders } from "../../../api/helpers";
import { periscopeThemeKey } from "../../../constants";
import mainStyles from "../../../styles";
import DisplayHeading from "../../shared/display-heading";
import { enableDisableWebhook, listWebhooks } from "api/endpoints/webhooks";
import { SnackbarContext } from "context/snackbar-context";
import { toUUID } from "helpers/s12-helpers";

interface WebhooksTabPros {
  regionCode: string;
  roleId: string;
}

function WebhooksTab({ regionCode, roleId }: WebhooksTabPros) {
  const isDarkTheme = window.localStorage.getItem(periscopeThemeKey);
  const theme = useTheme();
  const classes = mainStyles();
  const { showError } = useContext(SnackbarContext);
  const [showLoadingSpinner, setShowLoadingSpinner] = useState(false);
  const [orgWebhooks, setOrgWebhooks] = useState(
    new AdminListWebhooksResponse(),
  );
  const [selectedRecord, setSelectedRecord] = useState(
    {} as WebhookResponse | null,
  );
  const [showCfgDialog, setShowCfgDialog] = useState(false);
  const [reactJsonTheme] = useState<ThemeKeys>(
    isDarkTheme === "true" ? "hopscotch" : "rjv-default",
  );

  const handleCfgClickOpen = (record: WebhookResponse) => {
    setSelectedRecord(record);
    setShowCfgDialog(true);
  };

  const handleCfgClickClose = () => {
    setSelectedRecord(null);
    setShowCfgDialog(false);
  };

  const handleToggleClickOpen = (record: WebhookResponse) => {
    enableDisableWebhook(
      {
        organisationID: record.organisationId,
        webhookID: record.webhookId,
        enabled: !record.enabled,
      },
      callOptionsHeaders(regionCode, roleId),
    )
      .then(() => {
        webhooksList();
      })
      .catch(showError)
      .finally(() => {
        setShowLoadingSpinner(false);
      });
  };

  const webhooksList = () => {
    setShowLoadingSpinner(true);
    listWebhooks(
      { organisationID: toUUID(roleId) },
      callOptionsHeaders(regionCode, roleId),
    )
      .then((res) => {
        setOrgWebhooks(res);
      })
      .catch(showError)
      .finally(() => {
        setShowLoadingSpinner(false);
      });
  };

  useEffect(() => {
    webhooksList();
  }, []);

  if (showLoadingSpinner) {
    return (
      <Grid container>
        <Grid item xs={12} sx={classes.paddingTopSmall}>
          <DisplayHeading>Integrations</DisplayHeading>
          <Table>
            <TableRow>
              <LinearProgress />
            </TableRow>
          </Table>
        </Grid>
      </Grid>
    );
  }

  if (orgWebhooks.webhook.length === 0 && !showLoadingSpinner) {
    return (
      <Grid container>
        <Grid item xs={12} sx={classes.paddingTopSmall}>
          <DisplayHeading>Webhooks</DisplayHeading>
          <TableRow>
            <TableCell colSpan={5} data-testid="no-webhooks-message">
              <b>{"This organisation doesn't have any webhooks"}</b>
            </TableCell>
          </TableRow>
        </Grid>
      </Grid>
    );
  }

  const configDialog = (rawDocument: WebhookResponse) => {
    const s = JSON.stringify(rawDocument, (_, v) =>
      typeof v === "bigint" ? v.toString() : v,
    );
    const jsonDocument = JSON.parse(s);

    return (
      <Dialog
        open={showCfgDialog}
        onClose={handleCfgClickClose}
        fullWidth={true}
        maxWidth="lg"
      >
        <DialogTitle>
          <Typography variant="h6">
            Configuration for {jsonDocument?.webhookId}
          </Typography>
        </DialogTitle>
        <DialogContent style={{ whiteSpace: "pre-wrap" }}>
          <ReactJson
            theme={reactJsonTheme}
            src={jsonDocument}
            displayDataTypes={false}
          />
        </DialogContent>
      </Dialog>
    );
  };

  return (
    <Grid container>
      <Grid item xs={12} sx={classes.paddingTopSmall}>
        <DisplayHeading>Webhooks</DisplayHeading>
      </Grid>
      {showCfgDialog && selectedRecord !== null && configDialog(selectedRecord)}
      <Grid item xs={12}>
        <Box
          style={{
            borderColor: theme.palette.divider,
            borderWidth: "1px",
            borderStyle: "solid",
            borderRadius: "5px",
            margin: "20px",
            alignItems: "center",
          }}
        >
          <TableContainer>
            <Table size="medium">
              <TableHead>
                <TableRow>
                  <TableCell
                    className={clsx(classes.tableCell, classes.leftCell)}
                    align="left"
                  >
                    Webhook
                  </TableCell>
                  <TableCell
                    sx={{
                      ...classes.tableCell,
                      ...classes.leftCell,
                      ...classes.tableLeftColPadding,
                      ...classes.tableRightColPadding,
                    }}
                    align="center"
                  >
                    Enabled
                  </TableCell>
                  <TableCell
                    sx={{
                      ...classes.tableCell,
                      ...classes.leftCell,
                      ...classes.tableLeftColPadding,
                      ...classes.tableRightColPadding,
                    }}
                    align="center"
                  >
                    View
                  </TableCell>
                  <TableCell
                    sx={{
                      ...classes.tableCell,
                      ...classes.leftCell,
                      ...classes.tableLeftColPadding,
                      ...classes.tableRightColPadding,
                    }}
                    align="center"
                  >
                    Enable/Disable
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {orgWebhooks.webhook.map((webhook) => (
                  <TableRow key={webhook.webhookId}>
                    <TableCell
                      className={clsx(classes.tableCell, classes.leftCell)}
                      align="left"
                    >
                      {webhook.webhookId}
                    </TableCell>
                    <TableCell
                      className={clsx(classes.tableCell, classes.leftCell)}
                      align="center"
                    >
                      {JSON.stringify(webhook.enabled)}
                    </TableCell>
                    <TableCell
                      className={clsx(classes.tableCell, classes.leftCell)}
                      align="center"
                    >
                      <Button
                        sx={{
                          ...classes.spaceBottomSmall,
                          ...classes.spaceTopSmall,
                        }}
                        variant="contained"
                        color="primary"
                        onClick={() => handleCfgClickOpen(webhook)}
                      >
                        Configuration
                      </Button>
                    </TableCell>
                    <TableCell align="center">
                      <Button
                        sx={{
                          ...classes.spaceBottomSmall,
                          ...classes.spaceTopSmall,
                        }}
                        variant="contained"
                        color="primary"
                        onClick={() => handleToggleClickOpen(webhook)}
                      >
                        Toggle
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Grid>
    </Grid>
  );
}

export default WebhooksTab;
